import React, { useState } from "react";
import { ChevronDown, ChevronRight, PanelRightClose, PanelRightOpen, Pencil, Layers as LayersIcon, FolderOpen } from "lucide-react";
import { useT } from "../../lib/lang";
import { PlanificationObjectList } from "./PlanificationObjectList";
import { PlanificationObjectPropertiesForm } from "./PlanificationObjectPropertiesForm";

// Iter93 — Planification Phase 1 side panel -> Iter99 (Phase 2C §7):
// "drawing_tools" now renders the real object list (or the selected
// object's properties form in its place) instead of a placeholder;
// "geo_layers"/"saved_projects" deliberately KEEP their "coming soon"
// placeholder (spec §7.4: "Ne pas supprimer... Elles doivent conserver
// leur placeholder jusqu'à leur activation future").
export const PLANIFICATION_SECTIONS = [
  { key: "drawing_tools", labelKey: "planificationDrawingTools", icon: Pencil },
  { key: "geo_layers", labelKey: "planificationGeoLayers", icon: LayersIcon },
  { key: "saved_projects", labelKey: "planificationSavedProjects", icon: FolderOpen },
];

/**
 * `open` / `onToggleOpen` control the WHOLE panel's visibility (the
 * part that "maximizes map workspace" when collapsed). Each section
 * header independently expands/collapses its own body — purely local
 * state, nothing persisted (Planification has no durable storage yet).
 */
export const PlanificationSidePanel = ({
  open, onToggleOpen, features = [], selectedFeature, onSelectFeature,
  onSaveProperties, onCancelEdit, onDeleteSelected, onCenterOnMap,
}) => {
  const [t] = useT();
  const [expanded, setExpanded] = useState(() => new Set(["drawing_tools"]));

  const toggleSection = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="planification-panel-expand"
        onClick={onToggleOpen}
        title={t.planificationDrawingTools}
        aria-label={t.planificationDrawingTools}
        className="shrink-0 w-9 flex items-center justify-center border-l border-zinc-800 bg-[#0b0f14] text-zinc-500 hover:text-emerald-300 hover:bg-zinc-900 transition-colors"
      >
        <PanelRightOpen className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div
      className="shrink-0 w-full lg:w-72 border-l border-zinc-800 bg-[#0b0f14] flex flex-col overflow-y-auto"
      data-testid="planification-side-panel"
    >
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-800">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
          {t.planification}
        </span>
        <button
          type="button"
          data-testid="planification-panel-collapse"
          onClick={onToggleOpen}
          title="Replier"
          aria-label="Collapse"
          className="text-zinc-500 hover:text-emerald-300 p-1"
        >
          <PanelRightClose className="w-4 h-4" />
        </button>
      </div>

      {PLANIFICATION_SECTIONS.map((section) => {
        const Icon = section.icon;
        const isOpen = expanded.has(section.key);
        const label = t[section.labelKey] || section.key;
        const isDrawingTools = section.key === "drawing_tools";
        return (
          <div key={section.key} className="border-b border-zinc-800/60" data-testid={`planification-section-${section.key}`}>
            <button
              type="button"
              onClick={() => toggleSection(section.key)}
              aria-expanded={isOpen}
              data-testid={`planification-section-toggle-${section.key}`}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-xs font-semibold text-zinc-300 hover:text-emerald-300 hover:bg-zinc-900/60 transition-colors"
            >
              <Icon className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
              <span className="flex-1">{label}{isDrawingTools && features.length > 0 ? ` (${features.length})` : ""}</span>
              {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
            </button>
            {isOpen && isDrawingTools && (
              <div data-testid="planification-section-body-drawing_tools">
                {selectedFeature ? (
                  <PlanificationObjectPropertiesForm
                    feature={selectedFeature}
                    onSave={onSaveProperties}
                    onCancel={onCancelEdit}
                    onDelete={onDeleteSelected}
                    onCenterOnMap={onCenterOnMap}
                  />
                ) : (
                  <PlanificationObjectList features={features} selectedId={null} onSelect={onSelectFeature} />
                )}
              </div>
            )}
            {isOpen && !isDrawingTools && (
              <div className="px-3 pb-3 text-[11px] text-zinc-500 italic" data-testid={`planification-section-body-${section.key}`}>
                {t.planificationComingSoon}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
